"use client";

/**
 * Widget-specific account hook for external wallet connections via bridge.
 *
 * Returns the SAME interface as useParaAccount so SwapTokensForm can consume
 * it interchangeably. Reads from @solana/wallet-adapter-react instead of Para SDK.
 *
 * Used only when WidgetWalletProvider is mounted (widget routes in iframe).
 */

import { useMemo } from "react";
import { useWallet, useConnection } from "@solana/wallet-adapter-react";
import { Connection } from "@solana/web3.js";

/**
 * Return type matches useParaAccount exactly for interchangeability.
 * See hooks/use-appkit-account.tsx for the canonical interface.
 */
export interface WidgetAccountResult {
  /** Wallet public key as base58 string, or undefined if not connected */
  address: string | undefined;
  /** CAIP-2 address format: "solana:{address}" */
  caipAddress: string | undefined;
  /** Whether a wallet is connected via the bridge adapter */
  isConnected: boolean;
  /** Always true for widget external wallets (Phantom/Solflare via bridge) */
  isExternalSolana: boolean;
  /** Always false for widget (Solana-only, no EVM support) */
  isExternalEvm: boolean;
  /** Solana RPC connection (from wallet-adapter's ConnectionProvider) */
  connection: Connection;
  /** Wallet type - always "SOLANA" when connected via widget bridge */
  walletType: "SOLANA" | "EVM" | undefined;
}

export function useWidgetAccount(): WidgetAccountResult {
  const { publicKey, connected } = useWallet();
  const { connection } = useConnection();

  return useMemo(() => {
    const address = publicKey?.toBase58();

    return {
      // Required fields consumed by SwapTokensForm
      address,
      caipAddress: address ? `solana:${address}` : undefined,
      isConnected: connected && !!publicKey,
      walletType: connected && !!publicKey ? "SOLANA" : undefined,

      // Additional fields for full interface parity
      isExternalSolana: true, // Widget external wallets are always "external Solana"
      isExternalEvm: false, // Widget is Solana-only, no EVM support
      // NOTE: public endpoint from wallet-adapter ConnectionProvider (clusterApiUrl),
      // NOT our RPC proxy. Parity-only; swap flow builds its own proxy connection
      // and does not use this field.
      connection,
    };
  }, [publicKey, connected, connection]);
}
